import { useRouter } from 'next/router';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import headerdataPreval from '@integrations/preval/headerdata.preval';

/* Components */
import { getLayout } from '@components/PageLayouts/Layout/Standard';

/* Apis */
import { reviews } from '@api/apis';

/* Constants */
import { PAGE_TYPE_ALL_SERVICES } from '@constants/pages';
import { SEVEN_DAYS } from '@constants/CacheRevalidate';
import { HERO_CAT_CHIP_ORDER } from '@scenes/ServicesPage/servicespage.constants';

/* Components */
import ServicesPage from '@scenes/ServicesPage';

/* Types */
import { Children } from 'types/base.types';
import { TCategories, TServicesPageProps } from '@scenes/ServicesPage/servicespage.types';

/* Dynamic */
const SkeletonLoadingServicesDynamic = dynamic(() => import('@components/Loaders/Skeleton/Skeleton.Services'), { ssr: false });
/*
 * Our base Services Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */

function AllServicesPage(props: TServicesPageProps): Children {
  const router = useRouter();
  if (router.isReady) {
    return <ServicesPage {...props} />;
  }

  return <SkeletonLoadingServicesDynamic />;
}

AllServicesPage.pageAttributes = { page: PAGE_TYPE_ALL_SERVICES };
AllServicesPage.Layout = getLayout({ promo: true, footer: true });

export default AllServicesPage;

/**
 * - Get sku data
 * - Get reviews for sku
 * - Get routes for navigation (predone for rank/column)
 *
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns {Promise<*>}
 */
export const getStaticProps: GetStaticProps = async context => {
  const { data } = await reviews.reviewsPage();
  /* Get the popular skus */
  const popularSkus = headerdataPreval.servicesMenu.reduce((ps, cat) => {
    const pSkus = cat?.categories?.find(c => c.name === 'Popular Services');
    const getData = (matchedCat: { skus: TCategories[] }) =>
      matchedCat.skus.filter(s => s.id).map(s => ({ name: s.name, image: s.image, link: s?.link }));
    const skus = pSkus ? getData(pSkus) : false;

    if (skus) {
      return skus;
    }

    return ps;
  }, {});

  /* Get the categories minus popular */
  const categories = headerdataPreval.servicesMenu.reduce((cats, cat) => {
    const category = cat?.categories
      ?.filter(c => c.name !== 'Popular Services')
      .map(c => ({
        image: `/_nextassets_/images/categories/small/cat-${c.id}.png`,
        name: c.name,
        id: c.id,
        link: c?.link || `/tech-support/for/${c.seoName}`,
      }));

    return category ? cats.concat(category) : cats;
  }, [] as TCategories[]);

  // Sort the categories array based on the HERO_CAT_CHIP_ORDER array
  categories.sort((a, b) => {
    const indexA = HERO_CAT_CHIP_ORDER.indexOf(a.id);
    const indexB = HERO_CAT_CHIP_ORDER.indexOf(b.id);

    // If a.id is not found in HERO_CAT_CHIP_ORDER, assign a high sort index
    if (indexA === -1) return 1;
    // If b.id is not found in HERO_CAT_CHIP_ORDER, assign a low sort index
    if (indexB === -1) return -1;

    // Otherwise, sort based on the indices in HERO_CAT_CHIP_ORDER
    return indexA - indexB;
  });

  return {
    props: {
      popularSkus,
      categories,
      reviews: data?.reviews?.skus?.slice(0, 10) || [],
    },
    revalidate: SEVEN_DAYS,
  };
};
